import BaseService from '../base-service';

// Utils
import { formatTagsForDisplay } from 'libs/utils/objects';

// Constants
import { API_BASE_PATH } from 'libs/utils/constants';

/** @const {String} TAGS_BASE_PATH Tags base endpoint. Interpolation: eventId */
const TAGS_BASE_PATH = `${API_BASE_PATH}/events/{{eventId}}/tags`;

/** @const {String} TAG_LIST_ENDPOINT Endpoint for tag list. Interpolation: eventId, documentId */
const TAG_DOC_ENDPOINT = `${API_BASE_PATH}/events/{{eventId}}/docbyid/{{documentId}}/tags`;

/** @const {String} RENAME_TAG_ENDPONIT Endpoint for tag rename. Interpolation: eventId */
const RENAME_TAG_ENDPONIT = `${TAGS_BASE_PATH}/rename`;

/** @const {String} DELETE_TAG_ENDPOINT Endpoint for tag deletion. Interpolations: eventId, tag */
const DELETE_TAG_ENDPOINT = `${TAGS_BASE_PATH}/{{tag}}`;

/** @const {String} UNTAG_ENDPOINT Endpoint for untagging. Interpolations: eventId, tag */
const UNTAG_ENDPOINT = `${TAGS_BASE_PATH}/{{tag}}/remove`;

/**
 * Provide service related to Content Hubs Tags
 *
 * @example
 * import TagService from 'libs/services/content-hub/tag';
 * ...
 * const tagService = new TagService();
 */
export default class TagsService extends BaseService {
    /**
     * Fetches all tags for a given content hub
     *
     * @param {String} contentHubId
     *
     * @returns {Promise<Object>} A list of tags for a multi select
     */
    async getAll(contentHubId) {
        const url = this.buildUrl(TAGS_BASE_PATH, { eventId: contentHubId });
        const { data } = await this.get(url, { params: { with_counts: true } });
        return data;
    }

    /**
     * Update the tags of a given content on a given content hub.
     * @param {String} contentHubId
     * @param {String} documentId
     * @param {Object[]} tags
     * @returns the new tags for that content
     */
    async updateTags(contentHubId, documentId, tags) {
        const tagValues = tags.map(tag => tag.value);
        const url = this.buildUrl(TAG_DOC_ENDPOINT, { eventId: contentHubId, documentId });
        const { data } = await this.post(url, tagValues);
        return formatTagsForDisplay(data);
    }

    /**
     * Creates or updates a new tag associated with a specific content hub.
     *
     * @param {string} contentHubId - The unique identifier of the content hub.
     * @param {string} tag - The name of the tag to create.
     * @param {string[]} ids - An array of IDs to associate with the new tag.
     *
     * @returns {Promise<Object>} A promise that resolves to the created tag object.
     */
    async createOrUpdateTag(contentHubId, tag, ids) {
        const url = this.buildUrl(TAGS_BASE_PATH, { eventId: contentHubId });
        const { data } = await this.post(url, { tag, ids });
        return data;
    }

    /**
     * Renames a tag associated with a specific content hub.
     *
     * @param {string} contentHubId - The unique identifier of the content hub.
     * @param {string} from - The current name of the tag to rename.
     * @param {string} to - The new name for the tag.
     *
     * @returns {Promise<Object>} A promise that resolves to the updated list of tags.
     */
    async renameTag(contentHubId, from, to) {
        const url = this.buildUrl(RENAME_TAG_ENDPONIT, { eventId: contentHubId });
        const { data } = await this.post(url, { from, to });
        return data;
    }

    /**
     * Deletes a list of tags associated with a specific content hub.
     *
     * @param {string} contentHubId - The unique identifier of the content hub.
     * @param {string} tag - The name of the tag to delete
     *
     * @returns {Promise<import('axios').AxiosResponse>} A promise that resolves when the tags have been successfully deleted.
     */
    async deleteTag(contentHubId, tag) {
        const url = this.buildUrl(DELETE_TAG_ENDPOINT, { eventId: contentHubId, tag });
        return await this.delete(url);
    }

    /**
     * Unlinks items from a tag associated with a specific content hub.
     *
     * @param {string} contentHubId - The unique identifier of the content hub.
     * @param {string} tag - The name of the tag to unlink.
     * @param {string[]} ids - An array of IDs to unlink from the tag.
     *
     * @returns {Promise<void>} A promise that resolves when the items have been successfully unlinked.
     */
    async unlinkItems(contentHubId, tag, ids) {
        const url = this.buildUrl(UNTAG_ENDPOINT, { eventId: contentHubId, tag });
        const { data } = await this.post(url, { ids });

        return data;
    }
}
